import {
  ChangeDetectionStrategy, Component, computed, effect, EffectRef, input, InputSignal, model, ModelSignal, output, OutputEmitterRef, Signal, untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { AutocompleteComponent } from '@libs/form/components';
import { AutocompleteInputModel, AutocompleteOptionModel } from '@libs/form/models';
import { CustomerModel } from '@libs/account/models';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'ui-customer-autocomplete',
  templateUrl: './customer-autocomplete.component.html',
  styleUrls: ['./customer-autocomplete.component.scss'],
  imports: [
    AutocompleteComponent,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerAutocompleteComponent {
  private readonly controlValue: Signal<string | undefined>;
  private readonly syncControlValueEffect: EffectRef;
  private readonly syncCustomerInputEffect: EffectRef;

  public readonly customer: ModelSignal<CustomerModel | undefined> = model<CustomerModel | undefined>();
  public readonly customers: InputSignal<CustomerModel[]> = input<CustomerModel[]>([]);
  public readonly label: InputSignal<string> = input<string>('');
  public readonly selectChange: OutputEmitterRef<CustomerModel | undefined> = output<CustomerModel | undefined>();

  public readonly control: UntypedFormControl = new UntypedFormControl();

  public readonly customerAutocomplete: Signal<AutocompleteInputModel> = computed<AutocompleteInputModel>(() => {
    const customers: CustomerModel[] = this.customers();
    const customer: CustomerModel | undefined = untracked(this.customer);
    const label: string = untracked(this.label);

    const options: AutocompleteOptionModel[] = customers
      .map((customer: CustomerModel) => {
        const nameParts: string[] = [
          customer.id,
          customer.name,
          customer.address ? `${customer.address.postalCode} ${customer.address.locality}` : '',
        ];

        return {
          label: nameParts.filter(x => Boolean(x)).join(' - '),
          value: customer.id,
        };
      });

    return {
      type: 'autocomplete',
      name: 'autocomplete_customer',
      label: label,
      options,
      value: customer?.id ?? options[0]?.value ?? '',
      attributes: {
        required: true,
        readOnly: options.length < 2,
        spellcheck: false,
      },
      hint: '* Pflichtfeld',
    };
  });

  public constructor() {
    this.controlValue = toSignal(this.control.valueChanges.pipe(distinctUntilChanged()));

    this.syncControlValueEffect = effect(() => {
      const value: string | undefined = this.controlValue();
      const customer: CustomerModel | undefined = untracked(this.customer);
      const customers: CustomerModel[] = untracked(this.customers);

      const current: CustomerModel | undefined
        = this.control.valid && value !== ''
          ? customers.find((customer: CustomerModel) => customer.id === value)
          : undefined;

      if (current !== undefined && customer?.id !== current.id) {
        this.customer.set(current);
        this.selectChange.emit(current);
      }
    });

    this.syncCustomerInputEffect = effect(() => {
      const customer: CustomerModel | undefined = this.customer();
      const options: AutocompleteOptionModel[] = untracked(this.customerAutocomplete).options;

      const option: AutocompleteOptionModel | undefined = options.find(
        (item: AutocompleteOptionModel) => item.value === customer?.id,
      );

      this.control.setValue(option?.label, { emitEvent: false });
    });
  }
}
